import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button, GhostButton, MODAL_Z_INDEX, Toast, fullViewportFixed, useModalStackEntry, vars } from '@agendor/ui';
import { useToast } from '../toast-context';

const Overlay = styled.div<{ $hidden: boolean }>`
    ${fullViewportFixed}
    display: ${(p) => (p.$hidden ? 'none' : 'flex')};
    align-items: center;
    justify-content: center;
    z-index: ${MODAL_Z_INDEX};
    padding: 20px;

    @media (max-width: 600px) {
        padding: 0;
    }
`;

const ModalContainer = styled.div<{ $size: 'default' | 'wide' }>`
    background-color: ${vars.backgroundColor};
    border-radius: 12px;
    width: 100%;
    max-width: ${(props) => (props.$size === 'wide' ? '820px' : '500px')};
    max-height: 90vh;
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    box-shadow: ${vars.modalShadow};

    @media (max-width: 600px) {
        max-width: 100%;
        max-height: 100dvh;
        height: 100dvh;
        border-radius: 0;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        box-shadow: none;
    }
`;

const ModalHeader = styled.div`
    min-height: ${vars.navbarHeight};
    padding: 0 24px;
    background-color: rgba(${vars.dangerTintSoftRgb}, 0.95);
    border-bottom: 1px solid ${vars.dividerColor};
    display: flex;
    align-items: center;
    flex-shrink: 0;
    box-sizing: border-box;
    position: relative; /* containing block for the absolute-centered title */
    font-size: 0.95rem;

    @media (max-width: 600px) {
        position: sticky;
        top: 0;
        z-index: 10;
    }
`;

const Title = styled.h2<{ $absoluteCenterOnMobile: boolean }>`
    margin: 0;
    font-size: 0.95rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
    display: inline-flex;
    align-items: center;

    /* pointer-events: none lets the back button under the centered title still receive clicks. */
    ${(props) =>
        props.$absoluteCenterOnMobile &&
        `
        @media (max-width: 600px) {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            pointer-events: none;
        }
    `}
`;

const BackButton = styled(GhostButton)`
    display: none;

    @media (max-width: 600px) {
        display: inline-flex;
        margin-right: 8px;
        position: relative;
        z-index: 1; /* above the absolute-centered title */
    }
`;

const ModalContent = styled.div`
    padding: 24px;
    flex: 1;
    overflow-y: auto;
    min-height: 0;

    @media (max-width: 600px) {
        flex: 1 0 auto;
        overflow-y: visible;
        min-height: 0;
    }
`;

const ModalFooter = styled.div`
    padding: 16px 24px;
    border-top: 1px solid ${vars.dividerColor};
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    background-color: ${vars.backgroundColorDark};
    border-radius: 0 0 12px 12px;
    flex-shrink: 0;
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0));

    @media (max-width: 600px) {
        border-radius: 0;
        margin-top: auto;
    }
`;

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: React.ReactNode;
    children: React.ReactNode;
    footer?: React.ReactNode;
    size?: 'default' | 'wide';
    /**
     * When set, the modal renders a back button on the left of the header
     * (mobile only). Title becomes absolutely centered to the navbar so it
     * stays put regardless of the back button's width.
     */
    onBack?: () => void;
    'data-testid'?: string;
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = 'default',
    onBack,
    'data-testid': testId,
}) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const { modalToast, closeModalToast } = useToast();
    const { id, isTop, isOccluded } = useModalStackEntry(isOpen, 'modal');
    const containerId = `modal-container-${id}`;
    const footerId = `modal-footer-${id}`;

    useEffect(() => {
        if (!isOpen || !isTop) return;
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, isTop, onClose]);

    const mouseDownTarget = useRef<EventTarget | null>(null);

    const handleMouseDown = (e: React.MouseEvent) => {
        mouseDownTarget.current = e.target;
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        if (e.target === overlayRef.current && mouseDownTarget.current === overlayRef.current) {
            onClose();
        }
        mouseDownTarget.current = null;
    };

    if (!isOpen) return null;

    return (
        <Overlay ref={overlayRef} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp} $hidden={isOccluded}>
            <ModalContainer id={containerId} data-testid={testId} $size={size}>
                <ModalHeader>
                    {onBack && (
                        <BackButton type="button" onClick={onBack} aria-label="Voltar" data-testid="modal-back">
                            Voltar
                        </BackButton>
                    )}
                    <Title $absoluteCenterOnMobile={!!onBack}>{title}</Title>
                </ModalHeader>
                <ModalContent>{children}</ModalContent>
                <ModalFooter id={footerId}>
                    {footer ?? (
                        <Button data-testid="modal-close" onClick={onClose}>
                            Fechar
                        </Button>
                    )}
                </ModalFooter>
            </ModalContainer>
            {isTop && (
                <Toast
                    message={modalToast.message}
                    type={modalToast.type}
                    onClose={closeModalToast}
                    footerId={footerId}
                    containerId={containerId}
                    scrollContainerId={containerId}
                    toastKey={modalToast.key}
                />
            )}
        </Overlay>
    );
};
